import { Eye, Monitor, Smartphone } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import type { CareerSitePreview, CareerSitePreviewInput } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { Segmented } from '../../../../components/Segmented';
import { Skeleton, SkeletonLines } from '../../../../components/Skeleton';
import { errorMessage, type ApiError } from '../../../../lib/api-client';
import { useSettledValue } from '../../../../lib/use-settled-value';
import { useCareerSitePreview } from '../../api/publishing-api';
import { hostOf } from './career-site-form';
import styles from './SitePreview.module.css';

type Device = 'desktop' | 'phone';

/** The preview redraws once typing pauses this long. */
const PREVIEW_SETTLE_MS = 500;

const DEVICES = [
  { value: 'desktop', label: 'Desktop' },
  { value: 'phone', label: 'Phone' },
] as const;

/**
 * The live site beside the settings (Option A): the public front page, drawn by the server's own page code from the
 * settings as typed, over today's open roles. It sits in a frame sandboxed with no permissions — no script, no form,
 * no pop-up — so nothing in it can run, send, or be followed; looking at it records nothing.
 */
export function SitePreview({ draft, siteUrl, isUnsaved }: { readonly draft: CareerSitePreviewInput; readonly siteUrl: string; readonly isUnsaved: boolean }): ReactNode {
  const [device, setDevice] = useState<Device>('desktop');
  const settled = useSettledValue(draft, PREVIEW_SETTLE_MS);
  const preview = useCareerSitePreview(settled);
  const isCatchingUp = preview.isFetching || JSON.stringify(settled) !== JSON.stringify(draft);
  const retry = (): void => void preview.refetch();

  return (
    <section className={styles.pane} aria-label="Preview of the career site">
      <div className={styles.bar}>
        <span className={styles.label}>
          <Eye size={16} aria-hidden />
          {isUnsaved ? 'Preview · not saved yet' : 'Preview'}
        </span>
        {preview.data && (
          <span className={styles.meta} aria-live="polite">
            <PreviewStatus data={preview.data} hasFailed={preview.error !== null && !preview.isFetching} isCatchingUp={isCatchingUp} onRetry={retry} />
          </span>
        )}
        <Segmented label="Preview size" value={device} onChange={setDevice} options={DEVICES} />
      </div>
      <div className={styles.stage} data-device={device}>
        <div className={styles.browser}>
          <div className={styles.chrome} aria-hidden>
            <span className={styles.dot} />
            <span className={styles.dot} />
            <span className={styles.dot} />
            <span className={styles.address}>
              {device === 'phone' ? <Smartphone size={12} /> : <Monitor size={12} />}
              {hostOf(siteUrl)}
            </span>
          </div>
          <PreviewFrame data={preview.data} error={preview.error} onRetry={retry} />
        </div>
      </div>
    </section>
  );
}

function PreviewStatus({
  data,
  hasFailed,
  isCatchingUp,
  onRetry,
}: {
  readonly data: CareerSitePreview;
  readonly hasFailed: boolean;
  readonly isCatchingUp: boolean;
  readonly onRetry: () => void;
}): ReactNode {
  if (hasFailed) {
    return (
      <>
        Couldn't update the preview — this is the last one.{' '}
        <button type="button" className={styles.retry} onClick={onRetry}>
          Try again
        </button>
      </>
    );
  }
  if (isCatchingUp) return 'Updating…';
  return `${data.openRoles} open ${data.openRoles === 1 ? 'role' : 'roles'} today`;
}

function PreviewFrame({ data, error, onRetry }: { readonly data: CareerSitePreview | undefined; readonly error: ApiError | null; readonly onRetry: () => void }): ReactNode {
  if (data) {
    // `sandbox` with no permissions: the page's links open new windows, which this frame may not, so nothing in it can
    // be followed, sent or run. The draft is escaped by the server like every public page.
    return <iframe className={styles.frame} title="Career site preview" sandbox="" srcDoc={data.html} />;
  }
  if (error) {
    return (
      <div className={styles.problem} role="alert">
        <p className={styles.problemText}>Couldn't draw the preview. {errorMessage(error)}</p>
        <Button size="sm" variant="outline" onClick={onRetry}>
          Try again
        </Button>
      </div>
    );
  }
  return (
    <div className={styles.loading}>
      <Skeleton shape="title" width="60%" />
      <SkeletonLines count={6} />
    </div>
  );
}
